import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import agent from '../../app/api/agent';
import { LegacyStorefrontStatus } from '../../app/models/LegacyMode';

interface LegacyModeContextValue {
    loading: boolean;
    isLegacy: boolean;
    status?: LegacyStorefrontStatus;
    refresh: () => Promise<void>;
}

const LegacyModeContext = createContext<LegacyModeContextValue>({
    loading: false,
    isLegacy: false,
    refresh: async () => undefined,
});

export const LegacyModeProvider = ({ children }: { children: ReactNode }) => {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<LegacyStorefrontStatus>();

    const refresh = async () => {
        setLoading(true);
        try {
            setStatus(await agent.Settings.legacyStatus());
        } catch {
            // A missing status must never lock a native merchant out of the app.
            setStatus(undefined);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const value = useMemo<LegacyModeContextValue>(() => ({
        loading,
        status,
        isLegacy: status?.storefrontMode?.toLowerCase() === 'legacy',
        refresh,
    }), [loading, status]);

    return <LegacyModeContext.Provider value={value}>{children}</LegacyModeContext.Provider>;
};

export const useLegacyMode = () => useContext(LegacyModeContext);
